import { useCallback } from 'react';
import { FuzzyList } from 'utils/fuzzy';
import { getBackendBoolean } from 'utils/fm';
import { getLiteral } from 'utils/getLiteral';
import { getDateFormatPlaceholder } from 'utils/dates';
import { BOOL_FIELD_OPTIONS, PROBABILITY_CONSTRAINTS } from '../constants';

/**
 * Custom hook for field formatting logic in entity filters
 *
 * This hook provides functionality to format field objects for use in filter components,
 * handling different data types and configuring input properties based on field characteristics.
 *
 * @param {Object} standardFieldsSchemaMap - Map of standard field schemas with configuration data
 * @param {string} principalFilter - The principal filter field name for hierarchical filtering
 * @param {Function} loadFuzzyOptions - Function to load options for fuzzy search fields
 * @param {Function} loadOptions - Function to load options for regular select fields
 * @param {Object} filters - Current filter values object
 * @param {Object} operators - Available operators for field filtering
 *
 * @returns {Object} Object containing field formatting functions
 * @returns {Function} returns.getFieldsFormatted - Function to format multiple fields
 */
const useFieldFormatting = (
    standardFieldsSchemaMap,
    principalFilter,
    loadFuzzyOptions,
    loadOptions,
    filters,
    operators,
) => {
    /**
     * Determines if a field should use fuzzy search based on its list type
     *
     * @param {string} list - The list type identifier
     * @param {Object} field - The field object containing fuzzy search configuration
     * @returns {boolean} Whether the field should use fuzzy search
     */
    const isFuzzy = useCallback((list, field) => {
        if (!list) return false;
        return (
            FuzzyList.includes(list.toLowerCase()) || getBackendBoolean(field.fuzzySearchValueList)
        );
    }, []);
    /**
     * Formats a single field object with appropriate input properties based on its data type
     *
     * @param {Object} field - The field object to format
     * @param {string} field.dataType - The data type of the field (bool, probability, singleValueList, etc.)
     * @param {string} [field.hint] - Optional hint text for the field
     * @param {Object} [field.inputAttrs] - Additional input attributes and configuration
     * @param {string} field.id - Unique identifier for the field
     * @param {string} field.fieldConfiguration - Configuration key for the field
     * @param {boolean} [field.isCrossFilter] - Whether this is a cross-entity filter
     * @param {Object} [crossEntityOperators] - Operators specific to cross-entity filtering
     *
     * @returns {Object} The formatted field object with configured inputProps
     */
    const getFieldFormatted = useCallback(
        (field, crossEntityOperators) => {
            // Create a copy of the field to avoid mutating the original
            const newField = { ...field };
            if (!newField.inputProps) newField.inputProps = {};

            // Set placeholder from hint if available
            if (field.hint) newField.inputProps.placeholder = field.hint;

            // Configure field properties based on data type
            switch (field.dataType) {
                case 'bool':
                    // Boolean fields: Set up options for true/false selection
                    newField.inputProps.options =
                        newField?.inputAttrs?.options ||
                        BOOL_FIELD_OPTIONS.map((opt) => ({
                            label: getLiteral(opt.label),
                            value: opt.value,
                        }));
                    break;

                case 'probability':
                    // Probability fields: Apply predefined constraints
                    newField.inputProps = PROBABILITY_CONSTRAINTS;
                    break;

                case 'singleValueList':
                case 'relatedValueList':
                case 'multipleValueList':
                    // List fields: Configure options loading and selection behavior
                    if (field?.generateOptions) {
                        // Use custom option generator if available
                        newField.inputProps.options = field.generateOptions() || [];
                        if (field.inputAttrs?.hasOwnProperty('isMulti')) {
                            newField.inputProps.isMulti = field.inputAttrs.isMulti;
                        }
                    } else if (field?.inputAttrs?.list && isFuzzy(field?.inputAttrs?.list, field)) {
                        // Configure fuzzy search for large option lists
                        newField.inputProps.isFuzzy = true;
                        newField.inputProps.isMulti = newField.inputAttrs.hasOwnProperty('isMulti')
                            ? newField.inputAttrs.isMulti
                            : true;
                        newField.inputProps.loadOptions = loadFuzzyOptions(field);
                        newField.inputProps.defaultOptions = true;
                        newField.inputProps.defaultSearch = field.inputAttrs?.defaultSearch || '';
                    } else {
                        // Standard multi-select configuration
                        newField.inputProps.isMulti = true;
                        if (field?.inputAttrs?.list) {
                            newField.inputProps.loadOptions = loadOptions(field);
                        }
                    }

                    // Apply custom filter options if specified
                    if (field?.inputAttrs?.filterOption) {
                        newField.inputProps.overrides = {
                            'react-select': {
                                filterOption: field?.inputAttrs?.filterOption,
                            },
                        };
                    }

                    // Set default placeholder for select fields
                    if (!newField.inputProps.placeholder) {
                        newField.inputProps.placeholder = getLiteral('label_selectone');
                    }

                    // Add "Select All" functionality for multi-select fields (non-fuzzy)
                    if (newField.inputProps.isMulti && !newField.inputProps.isFuzzy) {
                        newField.inputProps.selectAllLabel = getLiteral('label_selectall');
                    }

                    // Principal filter logic: Filter child options based on parent selection
                    // This creates hierarchical filtering where child fields only show options
                    // that belong to the selected parent
                    if (
                        standardFieldsSchemaMap?.[newField.fieldConfiguration]?.strParentField ===
                        principalFilter
                    ) {
                        newField.inputProps.overrides = {
                            'react-select': {
                                filterOption: ({ data }) => {
                                    if (!filters || !data) return true;

                                    // Find the principal filter's current value
                                    let principal = Object.keys(filters).find(
                                        (filterKey) =>
                                            filters[filterKey]?.fieldConfiguration ===
                                            principalFilter,
                                    );
                                    if (principal) principal = filters[principal]?.value?.[0];

                                    // Show option only if it belongs to the selected principal
                                    // or if no principal is selected
                                    return (
                                        (principal && principal === data?.idparent) || !principal
                                    );
                                },
                            },
                        };
                    }

                    // Apply input attribute overrides: Merge field-specific overrides with existing ones
                    // This allows for custom react-select configurations per field
                    if (field.inputAttrs?.overrides) {
                        if (newField.inputProps.overrides) {
                            // Merge existing overrides with new ones, giving priority to field-specific overrides
                            newField.inputProps.overrides = {
                                ...newField.inputProps.overrides,
                                ...field.inputAttrs.overrides,
                                'react-select': {
                                    ...newField.inputProps.overrides['react-select'],
                                    ...(field.inputAttrs.overrides?.['react-select'] || {}),
                                },
                            };
                        } else {
                            newField.inputProps.overrides = { ...field.inputAttrs.overrides };
                        }
                    }

                    // Apply CSS class configuration
                    if (field.inputAttrs?.className) {
                        newField.inputProps.className = field.inputAttrs.className;
                    }

                    // Apply dynamic CSS class generation
                    if (field.inputAttrs?.getClassName) {
                        newField.inputProps.className = field.inputAttrs.getClassName();
                    }

                    break;

                case 'text':
                    // Text fields: Set default search placeholder if none specified
                    if (!newField?.inputAttrs?.placeholder) {
                        newField.inputProps.placeholder = getLiteral('placeholder_search');
                    }
                    break;

                case 'date':
                case 'dateTime':
                case 'dateRange': {
                    // Date filters: follow user-visible date format (same fallback strategy as CRUD fields)
                    const userDateFormat = field.hint || getDateFormatPlaceholder();
                    if (!newField.inputProps.placeholder) {
                        newField.inputProps.placeholder = userDateFormat;
                    }
                    newField.inputProps.inputMaskFormat = userDateFormat;
                    break;
                }
            }

            // Set operator for advanced filters
            // Choose between cross-entity operators and regular operators based on filter type
            const finalOperators =
                !!crossEntityOperators && !field.isCrossFilter ? crossEntityOperators : operators;

            // Apply the appropriate operator if one exists for this field
            if (finalOperators && finalOperators[field.id]) {
                newField.operator = finalOperators[field.id];
            }

            return newField;
        },
        [
            filters,
            isFuzzy,
            loadFuzzyOptions,
            loadOptions,
            operators,
            principalFilter,
            standardFieldsSchemaMap,
        ],
    );

    /**
     * Formats multiple fields (currently handles single field, legacy naming)
     *
     * @param {Object} fields - The field object to format (despite the name, this handles a single field)
     * @param {Object} [crossEntityOperators] - Operators specific to cross-entity filtering
     *
     * @returns {Array} Array containing the formatted field object
     */
    const getFieldsFormatted = useCallback(
        (fields, crossEntityOperators) => {
            const formattedField = getFieldFormatted(fields, crossEntityOperators);
            return [formattedField];
        },
        [getFieldFormatted],
    );

    return {
        getFieldsFormatted,
    };
};

export default useFieldFormatting;
